import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
} from '@mui/material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { format, addMonths, startOfDay, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth } from 'date-fns';

interface InvoiceDateCalendarProps {
  selectedDates: Date[];
  onChange: (dates: Date[]) => void;
  minDate?: Date;
  maxDate?: Date;
  referenceDates?: Date[]; // Dates to highlight/show as reference (e.g., invoice dates when selecting timesheet dates)
  referenceLabel?: string; // Label for reference dates (e.g., "Invoice Dates")
}

const InvoiceDateCalendar: React.FC<InvoiceDateCalendarProps> = ({
  selectedDates,
  onChange,
  minDate = new Date(),
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
  referenceDates = [],
  referenceLabel = 'Reference Dates',
}) => {
  const [open, setOpen] = useState(false);
  const [tempSelectedDates, setTempSelectedDates] = useState<Date[]>(selectedDates);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    setTempSelectedDates(selectedDates);
  }, [selectedDates]);

  const handleDateClick = (date: Date) => {
    const dateStart = startOfDay(date);
    const index = tempSelectedDates.findIndex((d) => isSameDay(d, dateStart));

    if (index >= 0) {
      // Remove date if already selected
      setTempSelectedDates(tempSelectedDates.filter((_, i) => i !== index));
    } else {
      // Add date if not selected and within range
      if (dateStart >= startOfDay(minDate) && dateStart <= startOfDay(maxDate)) {
        setTempSelectedDates([...tempSelectedDates, dateStart].sort((a, b) => a.getTime() - b.getTime()));
      }
    }
  };

  const handleSave = () => {
    onChange(tempSelectedDates);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempSelectedDates(selectedDates);
    setOpen(false);
  };

  const handleRemoveDate = (dateToRemove: Date) => {
    const newDates = selectedDates.filter((d) => !isSameDay(d, dateToRemove));
    onChange(newDates);
  };

  const isDateSelected = (date: Date): boolean => {
    return tempSelectedDates.some((d) => isSameDay(d, date));
  };

  const isReferenceDate = (date: Date): boolean => {
    return referenceDates.some((d) => isSameDay(d, date));
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = direction === 'prev' 
        ? addMonths(prev, -1)
        : addMonths(prev, 1);
      
      // Don't navigate beyond min/max dates
      if (newMonth < startOfMonth(minDate)) {
        return startOfMonth(minDate);
      }
      if (newMonth > endOfMonth(maxDate)) {
        return endOfMonth(maxDate);
      }
      return newMonth;
    });
  };

  const renderCalendarGrid = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const firstDayOfWeek = monthStart.getDay();
    const emptyDays = Array(firstDayOfWeek).fill(null);

    return (
      <Box>
        {/* Month Navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <IconButton onClick={() => navigateMonth('prev')} disabled={currentMonth <= startOfMonth(minDate)}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="h6">
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={() => navigateMonth('next')} disabled={currentMonth >= endOfMonth(maxDate)}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {/* Weekday Headers */}
        <Grid container spacing={0.5} sx={{ mb: 1 }}>
          {weekDays.map((day) => (
            <Grid item xs={12 / 7} key={day}>
              <Typography variant="caption" align="center" fontWeight="bold">
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Calendar Grid */}
        <Grid container spacing={0.5}>
          {emptyDays.map((_, index) => (
            <Grid item xs={12 / 7} key={`empty-${index}`} />
          ))}
          {daysInMonth.map((day) => {
            const isSelected = isDateSelected(day);
            const isReference = isReferenceDate(day);
            const isDisabled = day < startOfDay(minDate) || day > startOfDay(maxDate);
            const isToday = isSameDay(day, new Date());

            return (
              <Grid item xs={12 / 7} key={day.toISOString()}>
                <Box
                  onClick={() => !isDisabled && handleDateClick(day)}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    backgroundColor: isSelected 
                      ? 'primary.main' 
                      : isReference 
                      ? 'info.light' 
                      : 'transparent',
                    color: isSelected 
                      ? 'primary.contrastText' 
                      : isReference 
                      ? 'info.contrastText' 
                      : isDisabled 
                      ? 'text.disabled' 
                      : 'text.primary',
                    border: isToday 
                      ? '2px solid' 
                      : isReference && !isSelected
                      ? '1px dashed' 
                      : 'none',
                    borderColor: isToday 
                      ? 'primary.main' 
                      : isReference 
                      ? 'info.main' 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isDisabled 
                        ? 'transparent' 
                        : isSelected 
                        ? 'primary.dark' 
                        : isReference
                        ? 'info.main'
                        : 'action.hover',
                    },
                  }}
                  title={isReference ? `${referenceLabel}: ${format(day, 'dd MMM yyyy')}` : undefined}
                >
                  <Typography variant="body2" fontWeight={isReference ? 'bold' : 'normal'}>
                    {format(day, 'd')}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="body2" color="textSecondary">
          Invoice Dates:
        </Typography>
        {selectedDates.length === 0 ? (
          <Typography variant="body2" color="textSecondary" fontStyle="italic">
            No dates selected
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {selectedDates.map((date, index) => (
              <Chip
                key={index}
                label={format(date, 'dd MMM yyyy')}
                size="small"
                onDelete={() => handleRemoveDate(date)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<CalendarTodayIcon />}
          onClick={() => {
            setTempSelectedDates(selectedDates);
            setOpen(true);
          }}
        >
          {selectedDates.length === 0 ? 'Select Dates' : 'Edit Dates'}
        </Button>
      </Box>

      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Select Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Paper sx={{ p: 2 }}>
              {renderCalendarGrid()}
            </Paper>
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Dates ({tempSelectedDates.length}):
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                {tempSelectedDates.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Click dates on the calendar to select
                  </Typography>
                ) : (
                  tempSelectedDates.map((date, index) => (
                    <Chip
                      key={index}
                      label={format(date, 'dd MMM yyyy')}
                      size="small"
                      color="primary"
                      onDelete={() => {
                        setTempSelectedDates(tempSelectedDates.filter((_, i) => i !== index));
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Dates ({tempSelectedDates.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceDateCalendar;
